import { AuthToken } from 'src/modules/auth/authToken';
import Axios from 'axios';
import config from 'src/config';
import { getLanguageCode } from 'src/i18n';
import { getHistory } from 'src/modules/store';
import Qs from 'qs';
import moment from 'moment';

const authAxios = Axios.create({
  baseURL: config.backendUrl,
  paramsSerializer: function (params) {
    return Qs.stringify(params, {
      arrayFormat: 'brackets',
      filter: (prefix, value) => {
        if (
          moment.isMoment(value) ||
          value instanceof Date
        ) {
          return value.toISOString();
        }

        return value;
      },
    });
  },
});

authAxios.interceptors.request.use(
  async function (options) {
    const token = AuthToken.get();

    if (token) {
      options.headers['Authorization'] = `Bearer ${token}`;
    }

    options.headers['Accept-Language'] = getLanguageCode();

    return options;
  },
  function (error) {
    console.log('Request error: ', error);
    return Promise.reject(error);
  },
);

// ─── Silent re-login on session expiry ───────────────────────────────────────
// The admin session JWT is short-lived (~24h). When a normal Bearer call 401s
// mid-use, try the HttpOnly remember-me cookie ONCE to mint a fresh JWT and
// replay the original request. A single-flight guard collapses concurrent 401s
// into one re-login attempt. The /auth/admin/* calls themselves are excluded so
// a 401 from sign-in / verify / remember-me can never recurse here.
const ADMIN_AUTH_PATHS = [
  '/auth/admin/sign-in',
  '/auth/admin/mfa/verify',
  '/auth/admin/mfa/resend',
  '/auth/admin/session/remember-me',
  '/auth/admin/sign-out',
];

let rememberMePromise = null;

function refreshSessionViaRememberMe() {
  if (!rememberMePromise) {
    rememberMePromise = authAxios
      .post(
        '/auth/admin/session/remember-me',
        {},
        { withCredentials: true },
      )
      .then((response) => response.data.token)
      .finally(() => {
        rememberMePromise = null;
      });
  }
  return rememberMePromise;
}

authAxios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = (error && error.config) || {};
    const status = error && error.response && error.response.status;
    const url = original.url || '';
    const isAdminAuthCall = ADMIN_AUTH_PATHS.some((path) =>
      url.includes(path),
    );

    if (
      status !== 401 ||
      isAdminAuthCall ||
      original.__rememberMeRetried
    ) {
      return Promise.reject(error);
    }

    try {
      const token = await refreshSessionViaRememberMe();

      AuthToken.set(token, true);

      original.__rememberMeRetried = true;
      original.headers = {
        ...(original.headers || {}),
        Authorization: `Bearer ${token}`,
      };

      return authAxios(original);
    } catch (rememberMeError) {
      // No / expired / revoked device cookie → genuinely logged out.
      AuthToken.set(null, true);
      getHistory().push('/auth/signin');
      return Promise.reject(error);
    }
  },
);

export default authAxios;
